/**
 * =============================================================================
 * config.js — KONFIGURASI GLOBAL + HELPER MATEMATIKA
 * =============================================================================
 * Semua angka "aturan main" dikumpulkan di sini agar mudah di-tune.
 * TIDAK ada state runtime — hanya konstanta & fungsi murni.
 * =============================================================================
 */

/** @typedef {'classic'|'sprint'|'marathon'|'survival'|'relay'} RaceMode */

/* ===========================================================================
 * CONFIG — konstanta game
 * =========================================================================== */
export const CONFIG = {
  /* --- Batas jumlah peserta --- */
  MIN_ANTS: 2,
  MAX_ANTS: 100,
  DEFAULT_ANTS: 8,

  /* --- Layout lintasan (nilai dasar; padding aktual adaptif) --- */
  LANE_HEIGHT: 46,
  ANT_SCALE: 1,
  /** Base vertical pad; actual pad is adaptive via getTrackPadding() */
  TRACK_PADDING_Y: 80,

  /* --- Skala gambar semut (diperbesar di mobile) --- */
  /** Min/max ant draw scale (boosted on mobile) */
  ANT_SCALE_MIN: 0.7,
  ANT_SCALE_MAX: 2.15,
  ANT_REF_LANE: 48,
  FINISH_LINE_WIDTH: 24,

  /** Track length multipliers by mode (world units) */
  DISTANCES: {
    classic: 3200,
    sprint: 1600,
    marathon: 5600,
    survival: 2200,
    relay: 2400,
  },

  /**
   * SPEED — parameter RNG murni (bukan skill/level).
   * Base speed range (world units / second) — pure cosmetic RNG, no stats
   */
  SPEED: {
    min: 95,
    max: 175,
    jitterAmp: 55,
    jitterFreqMin: 0.4,
    jitterFreqMax: 1.8,
    burstChance: 0.012,
    burstMult: 1.45,
    slowChance: 0.01,
    slowMult: 0.55,
    effectDuration: [0.35, 1.1],
  },

  /* --- Mode Survival & Relay --- */
  SURVIVAL_ELIM_INTERVAL: 4.5,
  RELAY_ROUNDS: 3,
  RELAY_POINTS: [10, 8, 6, 5, 4, 3, 2, 1],

  /* --- Kamera --- */
  CAMERA: {
    lerp: 4.2,
    lookAhead: 220,
    shakeDecay: 6,
  },

  /* --- Loop performa --- */
  TARGET_FPS: 60,
  MAX_DT: 0.05,
};

/* ===========================================================================
 * MODE_INFO — label & deskripsi UI untuk tiap mode balapan
 * =========================================================================== */
export const MODE_INFO = {
  classic: {
    label: 'Classic Race',
    hint: 'Balapan klasik ke garis finish. Pemenang murni RNG!',
  },
  sprint: {
    label: 'Sprint',
    hint: 'Lintasan pendek & cepat — siapa yang beruntung di start?',
  },
  marathon: {
    label: 'Marathon',
    hint: 'Lintasan panjang dengan banyak saling-salip dan comeback.',
  },
  survival: {
    label: 'Survival',
    hint: 'Peserta terakhir digugurkan tiap ronde sampai tersisa juara.',
  },
  relay: {
    label: 'Relay Tournament',
    hint: 'Beberapa ronde, akumulasi poin. Total tertinggi menang!',
  },
};

/* ===========================================================================
 * HELPER MATEMATIKA / UTILITAS
 * =========================================================================== */

/** Batasi angka n agar selalu di antara a dan b. */
export function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

/**
 * getTrackPadding(height)
 * Smaller padding on short/mobile screens so ants get more vertical room
 */
export function getTrackPadding(height) {
  if (height < 480) return 48;
  if (height < 640) return 56;
  if (height < 780) return 64;
  return CONFIG.TRACK_PADDING_Y;
}

/**
 * computeAntScale(height, width, laneCount)
 * Compute readable ant scale from viewport + lane count.
 * Few ants → bigger; many ants → still keep a readable floor on mobile.
 */
export function computeAntScale(height, width, laneCount) {
  const pad = getTrackPadding(height);
  const usable = Math.max(140, height - pad * 2);
  const lanes = Math.max(2, laneCount || 2);
  const laneH = usable / lanes;

  // Mobile / narrow screens need a stronger boost so sprites stay clear
  const mobile = width < 520 || height < 700;
  const verySmall = width < 400 || height < 560;
  const boost = verySmall ? 1.35 : mobile ? 1.22 : 1;

  // Prefer filling most of the lane, with a higher ceiling when few racers
  const few = lanes <= 6;
  const mid = lanes <= 14;
  const minS = mobile
    ? few
      ? 1.25
      : mid
        ? 0.95
        : CONFIG.ANT_SCALE_MIN
    : few
      ? 1.05
      : CONFIG.ANT_SCALE_MIN;
  const maxS = few ? CONFIG.ANT_SCALE_MAX : mid ? 1.65 : mobile ? 1.25 : 1.15;

  let scale = (laneH / CONFIG.ANT_REF_LANE) * boost;
  // Soft floor so dense races on phones don't become unreadable dots
  if (mobile && lanes > 20) {
    scale = Math.max(scale, 0.78);
  }
  return clamp(scale, minS, maxS);
}

/** Angka acak float di [a, b). */
export function rand(a = 0, b = 1) {
  return a + Math.random() * (b - a);
}

/** Angka acak integer inklusif di [a, b]. */
export function randInt(a, b) {
  return Math.floor(rand(a, b + 1));
}

/** Ambil 1 elemen acak dari array. */
export function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * shuffle(arr)
 * Fisher–Yates — mengembalikan salinan array yang diacak (asli tidak diubah).
 */
export function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * formatTime(sec)
 * Format detik → "12.34s" atau "1:05.20" untuk UI hasil/HUD.
 */
export function formatTime(sec) {
  if (sec == null || !Number.isFinite(sec)) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0
    ? `${m}:${s.toFixed(2).padStart(5, '0')}`
    : `${s.toFixed(2)}s`;
}

/** Buat string warna CSS hsl/hsla. */
export function hsl(h, s, l, a = 1) {
  return a < 1 ? `hsla(${h}, ${s}%, ${l}%, ${a})` : `hsl(${h}, ${s}%, ${l}%)`;
}
